import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IoMenu } from 'react-icons/io5';
import { breakpoints, colors, fonts } from '../styles/theme';


const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(247, 243, 236, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid ${colors.line};
`;

const HeaderInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 32px;
  height: 74px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0 20px;
    height: 62px;
  }
`;

const Brand = styled(Link)`
  font-family: ${fonts.display};
  font-size: 21px;
  font-weight: 600;
  letter-spacing: 0.14em;
  color: ${colors.accent};
  text-decoration: none;
  white-space: nowrap;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 17px;
  }
`;

const NavList = styled.nav`
  display: flex;
  align-items: center;
  gap: 34px;

  @media (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const StyledNavLink = styled(NavLink)`
  position: relative;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${colors.inkSoft};
  padding: 6px 0;
  transition: color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1px;
    background: ${colors.gold};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.25s ease;
  }

  &:hover {
    color: ${colors.accent};
  }

  &.active {
    color: ${colors.accent};

    &::after {
      transform: scaleX(1);
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: ${colors.accent};

  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    align-items: center;
  }
`;

const MobileDropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 200;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.line};
  box-shadow: 0 18px 30px rgba(42, 30, 21, 0.15);
  padding: 10px 24px 18px;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const MobileLink = styled(NavLink)`
  display: block;
  font-family: ${fonts.display};
  font-size: 19px;
  color: ${colors.accent};
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.line};

  &:last-child {
    border-bottom: none;
  }

  &.active {
    color: ${colors.gold};
  }
`;

const NAV_ITEMS = [
  { to: '/about', label: 'um mig' },
  { to: '/books', label: 'verk' },
  { to: '/cv', label: 'hafa samband' },
];

export function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <Header>
      <HeaderInner>
        <Brand to="/about">BIRGITTA BJÖRG</Brand>
        <NavList>
          {NAV_ITEMS.map((item) => (
            <StyledNavLink key={item.to} to={item.to}>
              {item.label}
            </StyledNavLink>
          ))}
        </NavList>
        <MenuButton aria-label="Toggle menu" onClick={() => setMenuOpen((open) => !open)}>
          <IoMenu size={30} />
        </MenuButton>
      </HeaderInner>
      <MobileDropdown $open={menuOpen}>
        {NAV_ITEMS.filter((item) => item.to !== '/about').map((item) => (
          <MobileLink key={item.to} to={item.to}>
            {item.label}
          </MobileLink>
        ))}
      </MobileDropdown>
    </Header>
  );
}
